<template>
  <div class="components-detail">
    <div class="competition-content">
      <div class="info content">
        <div class="section-title">作品详情</div>
        <div class="title">{{ worksDetail.name || '--' }}</div>
        <div class="plate-list">
          <span
            v-if="worksDetail.plateName"
            class="tag plate-tag"
            :style="getTagStyle(worksDetail.plateColor, '#e28947')"
          >
            {{ worksDetail.plateName }}
          </span>
          <span
            v-if="worksDetail.categoryName"
            class="tag category-tag"
            :style="getTagStyle(worksDetail.categoryColor, '#8b7c9f')"
          >
            {{ worksDetail.categoryName }}
          </span>
          <span v-if="Number(worksDetail.isVote || 0) === 1" class="tag selected-tag">已选作品</span>
        </div>
        <div class="meta-grid">
          <div v-if="worksDetail.activityName">
            <span class="meta-label">所属赛事</span>
            <span>{{ worksDetail.activityName }}</span>
          </div>
          <div v-if="authorName">
            <span class="meta-label">作者</span>
            <span>{{ authorName }}</span>
          </div>
          <div v-if="creatorTypeLabel">
            <span class="meta-label">参赛形式</span>
            <span>{{ creatorTypeLabel }}</span>
          </div>
          <div v-if="organizationName">
            <span class="meta-label">{{ organizationLabel }}</span>
            <span>{{ organizationName }}</span>
          </div>
          <div v-if="specialityLabel">
            <span class="meta-label">{{ specialityFieldLabel }}</span>
            <span>{{ specialityLabel }}</span>
          </div>
          <div v-if="educationLabel">
            <span class="meta-label">{{ educationFieldLabel }}</span>
            <span>{{ educationLabel }}</span>
          </div>
          <div v-if="mentorText && !isProfessionalParticipant">
            <span class="meta-label">指导老师</span>
            <span>{{ mentorText }}</span>
          </div>
          <div v-if="worksDetail.material">
            <span class="meta-label">材料</span>
            <span>{{ worksDetail.material }}</span>
          </div>
          <div v-if="sizeText">
            <span class="meta-label">尺寸</span>
            <span>{{ sizeText }}</span>
          </div>
          <div v-if="weightText">
            <span class="meta-label">重量</span>
            <span>{{ weightText }}</span>
          </div>
          <div v-if="worksDetail.workCount">
            <span class="meta-label">作品数量</span>
            <span>{{ worksDetail.workCount }}</span>
          </div>
        </div>
        <div v-if="descriptionText" class="description">
          <span class="meta-label">作品介绍</span>
          <p>{{ descriptionText }}</p>
        </div>
      </div>

      <div v-if="hasVideo" class="video content">
        <div class="section-title">作品视频</div>
        <video controls class="video-player">
          <source :src="worksDetail.video" type="video/mp4">
          您的浏览器不支持视频播放
        </video>
      </div>

      <div v-if="imageList.length" class="pic-list content">
        <div class="section-title">作品图片</div>
        <div class="image-grid">
          <div v-for="(img, index) in imageList" :key="index" class="image-card">
            <img :src="img" @error="handleImageError">
          </div>
        </div>
      </div>

      <div v-if="seriesItems.length" class="series content">
        <div class="section-title">系列作品</div>
        <div class="series-list">
          <div v-for="(item, index) in seriesItems" :key="index" class="series-item">
            <div v-if="item.imageList.length" class="series-image-grid">
              <div v-for="(img, imgIndex) in item.imageList" :key="`${index}-${imgIndex}`" class="series-image-card">
                <img :src="img" @error="handleImageError">
              </div>
            </div>
            <div class="series-name">{{ item.name || `子作品 ${index + 1}` }}</div>
            <div v-if="item.size" class="series-meta">尺寸：{{ item.size }}</div>
            <div v-if="item.weight" class="series-meta">重量：{{ item.weight }}</div>
            <div v-if="item.material" class="series-meta">材料：{{ item.material }}</div>
            <div v-if="item.description" class="series-meta">{{ item.description }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getWorksDetail } from "@/api/Vote";
import { userEditVoteWorks } from "@/api/UserCenter";
import { mapGetters } from "vuex";
import {
  replaceBrokenImage,
  resolveWorkDirectImageList,
  toDirectImageUrl,
} from "@/utils/workImage";
import { normalizeVoteMeta } from "@/utils/voteMeta";

const FALLBACK_IMAGE = require("@/assets/images/nopic.png");
const EXAMPLE_IMAGE_RE = /^https?:\/\/(www\.)?example\.com\//i;

export default {
  name: "WorkCompetitionDetail",
  data() {
    return {
      id: 0,
      worksDetail: {},
      extraData: null,
    };
  },
  computed: {
    ...mapGetters(["userInfo"]),
    participantProfile() {
      const profile = this.extraData?.participantProfile;
      return profile && typeof profile === "object" ? profile : {};
    },
    isProfessionalParticipant() {
      if (this.participantProfile.identity === "professional") {
        return true;
      }
      if (this.participantProfile.identity === "student") {
        return false;
      }
      return String(this.worksDetail.education || "").trim() === "非学生/在职人员";
    },
    authorName() {
      return this.extraData?.author?.name || this.worksDetail.realName || "";
    },
    creatorTypeLabel() {
      if (this.extraData?.creatorType === "team" || this.worksDetail.creatorType === 1) {
        return "团队";
      }
      if (this.extraData?.creatorType === "personal" || this.worksDetail.creatorType === 0) {
        return "个人";
      }
      return "";
    },
    organizationLabel() {
      return this.isProfessionalParticipant ? "当前单位/公司" : "所在院校";
    },
    organizationName() {
      if (this.isProfessionalParticipant) {
        return String(
          this.participantProfile.companyName || this.participantProfile.company || ""
        ).trim();
      }
      return this.worksDetail.schoolName || "";
    },
    specialityFieldLabel() {
      return this.isProfessionalParticipant ? "从业方向/领域" : "专业";
    },
    specialityLabel() {
      if (this.isProfessionalParticipant) {
        return String(
          this.participantProfile.industryField || this.worksDetail.speciality || ""
        ).trim();
      }
      return String(this.participantProfile.major || this.worksDetail.speciality || "").trim();
    },
    educationFieldLabel() {
      return this.isProfessionalParticipant ? "身份" : "学历层次";
    },
    educationLabel() {
      if (this.isProfessionalParticipant) {
        return "非学生/在职人员";
      }
      return String(
        this.participantProfile.educationLevel || this.worksDetail.education || ""
      ).trim();
    },
    mentorText() {
      return [this.worksDetail.mentor, this.worksDetail.mentorSecond, this.worksDetail.mentorThird]
        .filter(Boolean)
        .join(" / ");
    },
    sizeText() {
      if (this.worksDetail.worksSize) {
        return this.worksDetail.worksSize;
      }
      const work = this.extraData?.work || {};
      if (work.length != null && work.width != null && work.height != null) {
        const unit = work.unit || "cm";
        return `${work.length}${unit} x ${work.width}${unit} x ${work.height}${unit}`;
      }
      return "";
    },
    weightText() {
      return this.formatWeight(this.worksDetail.weight);
    },
    descriptionText() {
      return this.extraData?.work?.description || this.worksDetail.content || "";
    },
    hasVideo() {
      return Boolean(this.worksDetail.video && String(this.worksDetail.video).trim());
    },
    imageList() {
      return resolveWorkDirectImageList(this.worksDetail);
    },
    seriesItems() {
      const list = this.extraData?.work?.seriesItems || [];
      if (!Array.isArray(list)) {
        return [];
      }
      return list.map((item) => ({
        name: item.name || "",
        material: item.material || "",
        description: item.description || "",
        size: item.size || this.joinSize(item),
        weight: this.formatWeight(item.weight),
        imageList: this.resolveSeriesImageList(item),
      }));
    },
  },
  mounted() {
    this.id = this.$route.params.id;
    this.getWorksInfo();
  },
  methods: {
    getWorksInfo() {
      const request = this.$route.query.source === "user-center"
        ? userEditVoteWorks(this.id)
        : getWorksDetail(this.id, { uid: this.userInfo.uid | 0 });
      request.then((res) => {
        if (res.status === 200) {
          this.worksDetail = {
            ...(res.data || {}),
            ...normalizeVoteMeta(res.data || {}),
          };
          this.extraData = this.parseExtraData(this.worksDetail.extraJson);
        }
      });
    },
    parseExtraData(value) {
      if (!value) {
        return null;
      }
      try {
        return typeof value === "string" ? JSON.parse(value) : value;
      } catch (error) {
        return null;
      }
    },
    joinSize(item) {
      if (item.length == null || item.width == null || item.height == null) {
        return "";
      }
      const unit = item.unit || "cm";
      return `${item.length}${unit} x ${item.width}${unit} x ${item.height}${unit}`;
    },
    formatWeight(value) {
      const text = String(value == null ? "" : value).trim();
      if (!text) {
        return "";
      }
      if (/(kg|KG|Kg|千克|公斤)$/.test(text)) {
        return text;
      }
      return `${text}kg`;
    },
    resolveSeriesImageList(item) {
      return []
        .concat(Array.isArray(item?.imageList) ? item.imageList : [])
        .concat(String(item?.images || "").split(","))
        .map((image) => String(image || "").trim())
        .filter((image, index, list) => image && !EXAMPLE_IMAGE_RE.test(image) && list.indexOf(image) === index)
        .map((image) => toDirectImageUrl(image));
    },
    getTagStyle(color, fallbackColor = "#e28947") {
      return { background: color || fallbackColor };
    },
    handleImageError(event) {
      replaceBrokenImage(event, FALLBACK_IMAGE);
    },
  },
};
</script>

<style lang="scss">
.components-detail {
  background: #f7f8fa;
  min-height: 100vh;

  .competition-content {
    margin: 0 auto;
    padding: 24px 0 48px;
    width: 1280px;
    text-align: left;
  }

  .content {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 0 8px 5px rgb(0 0 0 / 9%);
    margin: 24px auto;
    padding: 24px;
  }

  .section-title {
    height: 30px;
    line-height: 30px;
    font-size: 18px;
    color: #000;
    margin-bottom: 18px;
    position: relative;
    text-indent: 12px;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 8px;
      width: 3px;
      height: 14px;
      background-color: #e28947;
    }
  }

  .title {
    color: #000;
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 12px;
  }

  .plate-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 18px;
  }

  .tag {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 4px;
    color: #fff;
    font-size: 12px;
  }

  .selected-tag {
    background: linear-gradient(135deg, #ffb06d, #ea7c44);
  }

  .meta-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 36px;
    margin-bottom: 18px;

    > div {
      color: #666;
      line-height: 24px;
      word-break: break-all;
    }
  }

  .meta-label {
    display: inline-block;
    min-width: 96px;
    color: #333;
    font-weight: 600;
  }

  .description {
    border-top: 1px solid #f0f0f0;
    padding-top: 16px;
    color: #666;
    line-height: 28px;

    p {
      margin: 8px 0 0;
      white-space: pre-wrap;
      word-break: break-all;
    }
  }

  .series-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  .series-item {
    border: 1px solid #f0f0f0;
    border-radius: 10px;
    padding: 16px;
    background: #fafafa;
  }

  .series-image-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 14px;
  }

  .series-image-card {
    border-radius: 8px;
    overflow: hidden;
    background: #f2f2f2;
    min-height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .series-image-card img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
  }

  .series-name {
    color: #222;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 10px;
  }

  .series-meta {
    color: #666;
    line-height: 24px;
    word-break: break-all;
  }

  .video-player {
    width: 100%;
    max-height: 800px;
    border-radius: 10px;
    background: #000;
  }

  .image-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  .image-card {
    border-radius: 10px;
    overflow: hidden;
    background: #f2f2f2;
    min-height: 220px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .image-card img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
  }
}
</style>
